import { FC } from 'react';
import NextLink from 'next/link';
import { useTranslation } from 'next-i18next';
import clsx from 'clsx';
import {
  Badge,
  Button,
  ListItem,
  ListItemProps,
  ListItemText,
  Typography,
  TypographyProps,
  styled,
  useTheme
} from '@mui/material';

interface OptionsMenuItemProps {
  name: string;
  active: boolean;
  link: string | { pathname: string; query: Record<string, string> };
  icon?: any;
  isDisabled?: boolean;
  handleOnClick(): void;
  listItemStyles?: ListItemProps['sx'];
  textStyles?: TypographyProps['sx'];
}

const MenuOptionButton = styled(Button)(({ theme }) => ({
  borderRadius: `${theme.spacing(0.65)} !important`,

  '.MuiButton-startIcon': {
    color: theme.sidebar.menuItemIconColor
  },

  '&.Mui-active,&:hover': {
    backgroundColor: theme.sidebar.menuItemBgActive,
    color: theme.sidebar.menuItemColorActive,

    '.MuiButton-startIcon': {
      color: theme.sidebar.menuItemIconColorActive
    }
  }
}));

export const OptionsMenuItem: FC<OptionsMenuItemProps> = ({
  active,
  icon: Icon,
  name,
  link,
  isDisabled,
  handleOnClick,
  listItemStyles,
  textStyles,
  ...rest
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const activeColor = active ? theme.colors.primary.main : 'inherit';

  return (
    <ListItem
      component="div"
      sx={{
        padding: 0,
        ...listItemStyles
      }}
      {...rest}
    >
      <NextLink href={link || ''} passHref>
        <MenuOptionButton
          disableRipple
          fullWidth
          className={clsx({ 'Mui-active': active })}
          onClick={!isDisabled && handleOnClick}
          startIcon={Icon && <Icon color={theme.sidebar.menuItemIconColor} />}
          style={{
            color: `${isDisabled ? '#9e9e9e' : activeColor}`,
            cursor: isDisabled ? 'not-allowed' : 'pointer',
            borderRadius: 0,
            padding: theme.spacing(1.45, 1.5)
          }}
          LinkComponent={'a'}
        >
          <ListItemText>
            <Typography
              sx={{ textAlign: 'left', ...textStyles }}
              variant="body1"
            >
              {t(name)}
            </Typography>
          </ListItemText>

          {isDisabled && (
            <Badge
              color="primary"
              badgeContent="WIP"
              style={{ opacity: 0.7 }}
            />
          )}
        </MenuOptionButton>
      </NextLink>
    </ListItem>
  );
};
